// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useCallback, useEffect, useMemo, useState } from 'react';

import type { TourPlayingStyleEntry, TourPlayingStyles } from './types';

type Options = {
  apiBaseUrl: string;
  token: string | null | undefined;
  tourId: string | null | undefined;
  /** Changes when the tour's rounds change, so a new round's holes are compared too. */
  refreshKey: string;
};

type Result = {
  fieldSize: number;
  /** The player's tour profile, or null when there is none (no holes, or still loading). */
  styleFor: (playerId: string) => TourPlayingStyleEntry | null;
};

const key = (playerId: string) => playerId.trim().toLowerCase();

/**
 * Loads every player's profile for one tour, each compared against the tour's own field. The
 * profiles are an extra on the leaderboard, so a failed load leaves them out instead of showing
 * an error.
 */
export function useTourPlayingStyles({ apiBaseUrl, token, tourId, refreshKey }: Options): Result {
  const [styles, setStyles] = useState<TourPlayingStyles | null>(null);

  useEffect(() => {
    if (!token || !tourId) {
      setStyles(null);
      return;
    }

    const controller = new AbortController();
    fetch(`${apiBaseUrl}/api/tours/${tourId}/playing-styles`, {
      headers: { Authorization: `Bearer ${token}` },
      signal: controller.signal,
    })
      .then(async (response) => (response.ok ? ((await response.json()) as TourPlayingStyles) : null))
      .then((payload) => {
        if (!controller.signal.aborted) setStyles(payload);
      })
      .catch(() => {
        if (!controller.signal.aborted) setStyles(null);
      });

    return () => controller.abort();
  }, [apiBaseUrl, refreshKey, token, tourId]);

  const byPlayerId = useMemo(
    () => new Map((styles?.players ?? []).map((entry) => [key(entry.playerId), entry])),
    [styles]
  );
  const styleFor = useCallback((playerId: string) => byPlayerId.get(key(playerId)) ?? null, [byPlayerId]);

  return { fieldSize: styles?.fieldSize ?? 0, styleFor };
}
